import React, { useState, useEffect } from 'react';
import { ResumeForm } from './ResumeForm';
import { templates } from './templates';
import { PreviewHeader } from './components/PreviewHeader';
import { TemplatePreview } from './components/TemplatePreview';
import { AutoSave } from './components/AutoSave';
import { ResumeImportExport } from './components/ResumeImportExport';
import html2pdf from 'html2pdf.js';
import type { ResumeData } from './types';

export function ResumeBuilder() {
  const [resumeData, setResumeData] = useState<ResumeData | null>(() => {
    const saved = localStorage.getItem('resume-data');
    return saved ? JSON.parse(saved) : null;
  });
  const [step, setStep] = useState<'form' | 'preview'>('form');
  const [selectedTemplate, setSelectedTemplate] = useState('modern');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  useEffect(() => {
    if (resumeData) {
      localStorage.setItem('resume-data', JSON.stringify(resumeData));
      setLastSaved(new Date());
    }
  }, [resumeData]);

  const handleSubmit = (data: ResumeData) => {
    setResumeData(data);
    setStep('preview');
  };

  const generatePDF = () => {
    const element = document.getElementById('resume-preview');
    const opt = {
      margin: 1,
      filename: 'resume.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    };

    html2pdf().set(opt).from(element).save();
  };

  const SelectedTemplateComponent = templates.find(t => t.id === selectedTemplate)?.component;

  return (
    <div className="container mx-auto px-4 py-8">
      {step === 'form' ? (
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold">Create Your Resume</h1>
            <div className="z-40">
              <ResumeImportExport onImport={setResumeData} data={resumeData} />
            </div>
          </div>
          <ResumeForm onSubmit={handleSubmit} initialData={resumeData} />
          {lastSaved && <AutoSave lastSaved={lastSaved} />}
        </div>
      ) : (
        <div className="max-w-4xl mx-auto">
          <PreviewHeader 
            onEdit={() => setStep('form')}
            onDownload={generatePDF}
          />
          
          <div className="mb-8">
            <TemplatePreview
              selectedTemplate={selectedTemplate}
              onSelect={setSelectedTemplate}
            />
          </div>
          
          <div id="resume-preview" className="bg-white shadow-lg rounded-lg overflow-hidden">
            {SelectedTemplateComponent && resumeData && (
              <SelectedTemplateComponent data={resumeData} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}